/**
 * Presentation for the /scan station's resolve statuses (pure). Keyed by the FULL `ScanResolveStatus`
 * enum so a new backend status forces a UI decision here — drift-guarded by scan-lib.test.ts.
 */
import type {
  ReissueNotOwedReason,
  ScanResolvePlacement,
  ScanResolveResult,
  ScanResolveStatus,
} from '@fsp/contracts';
import type { components } from '@/lib/api/types.gen';
import { frontFamilyPlacement, placementBadge } from './label-lib';

export type ScanTone = 'success' | 'warn' | 'error' | 'info';

export interface ScanStatusView {
  title: string;
  tone: ScanTone;
  hint: string;
  /** Whether to show the resolved front/back design cards for this state. */
  showDesign: boolean;
}

export const SCAN_STATUS_VIEW: Record<ScanResolveStatus, ScanStatusView> = {
  OK: {
    title: 'Press this garment',
    tone: 'success',
    hint: 'Tap each side below as you press it.',
    showDesign: true,
  },
  DO_NOT_PRESS: {
    title: 'Do NOT press',
    tone: 'error',
    hint: 'This copy was cancelled or refunded after batching — set it aside.',
    showDesign: true,
  },
  ALREADY_PRINTED: {
    title: 'Already printed',
    tone: 'warn',
    hint: 'This batch is closed — the garment has already been produced.',
    showDesign: true,
  },
  NOT_IN_BATCH: {
    title: 'Not in this batch',
    tone: 'warn',
    hint: 'A surplus copy or a stale label — reprint labels from the current batch.',
    showDesign: true,
  },
  NOT_BATCHED: {
    title: 'Not batched yet',
    tone: 'info',
    hint: 'This garment isn’t on a ready or printed gang sheet yet.',
    showDesign: false,
  },
  LINE_NOT_FOUND: {
    title: 'Order line not found',
    tone: 'error',
    hint: 'The order or line no longer exists (deleted or purged).',
    showDesign: false,
  },
  NOT_DTF: {
    title: 'Cap — not a DTF garment',
    tone: 'info',
    hint: 'This line is produced as a cap, not on a gang sheet.',
    showDesign: false,
  },
  UNRECOGNIZED: {
    title: 'Unrecognized code',
    tone: 'error',
    hint: 'Not a Fast Screen Print garment tag.',
    showDesign: false,
  },
  STOCK_BIN: {
    title: 'Garment shelf bin',
    tone: 'info',
    hint: 'A finished-garment shelf bin label — what the stack is and how many are on hand.',
    showDesign: false,
  },
};

export function scanStatusView(status: ScanResolveStatus): ScanStatusView {
  return SCAN_STATUS_VIEW[status];
}

// ─────────────────────────────────────────────────────────────────────────────
// Press check-off at the scan station (Rollo Phase 3) — pure decision helpers.
// The station drives the EXISTING `POST /batches/:batchId/press` (per-copy owed-guard + settleLine +
// scope-keyed reverse); these functions just decide WHAT to send and WHEN a tap is allowed.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A placement is pressable at the scan station iff the scan is actionable (`OK`) AND the placement is
 * DTF. Screen-print is pressed coarsely from the SP worklist (not per garment here); any non-OK status
 * (already printed, do-not-press, surplus, …) is not something the heat-press operator checks off.
 */
export function isPressable(
  result: Pick<ScanResolveResult, 'status'>,
  placement: Pick<ScanResolvePlacement, 'method'>,
): boolean {
  return result.status === 'OK' && placement.method === 'dtf';
}

/**
 * The scanned garment is fully pressed when every DTF placement on it is pressed. Screen-print
 * placements are pressed elsewhere (SP worklist) so they don't gate the scan-station "done" chip.
 * False when the garment carries no DTF placement (nothing for this station to complete).
 */
export function garmentDtfDone(
  placements: ReadonlyArray<Pick<ScanResolvePlacement, 'method' | 'pressed'>>,
): boolean {
  const dtf = placements.filter((p) => p.method === 'dtf');
  return dtf.length > 0 && dtf.every((p) => p.pressed);
}

/**
 * The kiosk identity band's one big badge for a scanned garment — the code the operator matches the
 * transfer by. Derived from the FRONT-FAMILY placement (`front`, else left-chest/sleeve/…) through the
 * SAME helpers the printed label uses, so the band and the label can never name a garment differently:
 * a left-chest-only garment reads `LC` on both, not `LC` on the label and `—` on the screen.
 *
 * The style code is the live join (designId → code); a design without a code yet falls back to the
 * legacy first-two-letters of its name, and a back-only garment gets the `—` placeholder.
 */
export function garmentBadge(
  placements: ReadonlyArray<Pick<ScanResolvePlacement, 'location' | 'designId' | 'designName'>>,
  styleCodeByDesignId: ReadonlyMap<string, string>,
): string {
  const primary = frontFamilyPlacement(placements);
  if (!primary) return placementBadge(undefined);
  const styleCode = styleCodeByDesignId.get(primary.designId);
  return placementBadge({
    designName: primary.designName ?? '',
    ...(styleCode ? { styleCode } : {}),
  });
}

/** Body for `POST /batches/:batchId/press` toggling THIS placement's copy on the scanned garment. */
export interface PressBody {
  storeId: string;
  orderId: string;
  lineId: string;
  location: string;
  copyIndex: number;
  pressed: boolean;
}

/**
 * Build the press-toggle body from a resolved scan + the placement being tapped. `copyIndex` comes
 * straight from the placement (the API returns `owedRank - 1`, the same copy the durable-identity QR
 * was minted for) so the label and the press check-off can never disagree about which copy this is.
 * Returns `null` if the scan lacks the identity fields (defensive — callers only tap on an `OK` scan).
 */
export function pressBodyFor(
  result: Pick<ScanResolveResult, 'storeId' | 'orderId' | 'lineId'>,
  placement: Pick<ScanResolvePlacement, 'location' | 'copyIndex'>,
  pressed: boolean,
): PressBody | null {
  if (!result.storeId || !result.orderId || !result.lineId) return null;
  return {
    storeId: result.storeId,
    orderId: result.orderId,
    lineId: result.lineId,
    location: placement.location,
    copyIndex: placement.copyIndex,
    pressed,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Defect → reprint at the scan station (Rollo Phase 5) — pure decision helpers.
// A defect drives the EXISTING `POST /batches/:batchId/reissue` (draws an additive replacement blank at
// the reissue's OWN mark-printed) with the whole garment (owner decision: whole-garment, DTF-only). The
// undo = DELETE that reissue batch (safe — a not-yet-printed reissue owns no line + drew no blank).
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A scanned garment can be sent for reprint iff it resolved to a REAL, still-OWED batched garment —
 * status `OK` (ruined during pressing) or `ALREADY_PRINTED` (QC defect caught after production). Both
 * are the source states the reissue endpoint accepts (READY | PRINTED). Never for a do-not-press /
 * surplus / cap / unrecognized / not-yet-batched scan; also requires the identity + source batchId.
 *
 * ⚠️ OWED GUARD (money-critical): on a PRINTED batch the resolver reports `ALREADY_PRINTED` BEFORE it
 * reports `DO_NOT_PRESS`, so a CANCELLED / refunded garment on a printed batch masks as
 * `ALREADY_PRINTED`. Reprinting it would draw a billable replacement blank for a garment nobody owes.
 * So require the garment to still be owed: `owedRank <= copyOf` (`copyOf` = the line's owed count). `OK`
 * is only ever returned for an owed garment, so this is a no-op there and the real backstop for
 * `ALREADY_PRINTED`.
 */
export function canReprint(
  result: Pick<
    ScanResolveResult,
    'status' | 'storeId' | 'orderId' | 'lineId' | 'batchId' | 'owedRank' | 'copyOf'
  >,
): boolean {
  return reprintGate(result) === 'normal';
}

/**
 * How the reprint affordance should present for a scanned garment.
 *
 *  · `hidden`   — not a reprintable scan at all (wrong status, or missing identity/batch/rank).
 *  · `normal`   — a still-OWED garment: today's red "mark defective → reprint" panel.
 *  · `not-owed` — a REAL batched garment that nobody owes any more (refunded / cancelled). Reprinting
 *                 draws a billable replacement blank, so it needs a deliberate override.
 *
 * `canReprint` used to fold `not-owed` into `false`, which HID the panel — that made a goodwill reprint
 * impossible at the press, i.e. it shipped short. The server now enforces the owed gate and accepts a
 * per-garment override, so the kiosk must be able to SHOW the case rather than hide it.
 */
export function reprintGate(
  result: Pick<
    ScanResolveResult,
    'status' | 'storeId' | 'orderId' | 'lineId' | 'batchId' | 'owedRank' | 'copyOf'
  >,
): 'hidden' | 'normal' | 'not-owed' {
  if (result.status !== 'OK' && result.status !== 'ALREADY_PRINTED') return 'hidden';
  if (!result.storeId || !result.orderId || !result.lineId || !result.batchId) return 'hidden';
  if (result.owedRank == null || result.copyOf == null) return 'hidden';
  return result.owedRank <= result.copyOf ? 'normal' : 'not-owed';
}

/**
 * Idempotency-token state for the reprint button, keyed to the garment on screen.
 *
 * The intent is (scanned code, generation): the SAME code with an un-succeeded attempt must reuse its
 * token so a retry after a 502 dedupes server-side instead of minting a second billable reprint; a
 * DIFFERENT code — or a code revisited after a success cleared the token — is a new intent and must
 * mint, or the new garment's reprint would replay the previous batch and never get printed.
 */
/** Re-exported so the kiosk page keeps a single import surface for reprint concerns. */
export type { ReissueNotOwedReason };

export interface ReissueTokenState {
  token: string | null;
  code: string | null;
}

export function reissueTokenFor(
  state: ReissueTokenState,
  code: string,
  mint: () => string,
): ReissueTokenState {
  return state.token && state.code === code ? state : { token: mint(), code };
}

/** Body for `POST /batches/:batchId/reissue` reprinting the ONE scanned garment (whole garment, 1 copy). */
export interface ReissueBody {
  garments: Array<{
    storeId: string;
    orderId: string;
    lineId: string;
    copies: number;
    owedRanks?: number[];
    notOwedOverride?: { acknowledgedOwed: number; reason: ReissueNotOwedReason };
  }>;
  clientToken?: string;
}

/**
 * Where a defect report from this kiosk goes (P6).
 *
 *  · `hidden`  — not a reprintable scan at all.
 *  · `queue`   — the normal case: `POST /batches/reprint-cart/entries`, so the garment waits and
 *                merges onto ONE sheet with the cutter's and the office's reprints.
 *  · `reissue` — a NOT-OWED garment, which still mints its own sheet immediately.
 *
 * ⚠️ The `reissue` arm is not an oversight and must not be "unified" with the queue, D4
 * notwithstanding. The queue record has no `notOwedOverride` field — deliberately, because
 * `acknowledgedOwed` has to equal the LIVE owed count and a stored override goes stale — and the
 * flush's owed gate therefore has no override either. Queue a goodwill reprint and you get a row
 * that can NEVER be flushed: it 422s the whole sheet for every other station, blocks its order
 * indefinitely, and its only exit is a manager's dismiss, which un-blocks the order with the garment
 * still ruined. The immediate reissue route is the one path that can carry the override.
 */
export type ReprintDestination = 'hidden' | 'queue' | 'reissue';

export function reprintDestination(
  result: Pick<
    ScanResolveResult,
    'status' | 'storeId' | 'orderId' | 'lineId' | 'batchId' | 'owedRank' | 'copyOf'
  >,
): ReprintDestination {
  const gate = reprintGate(result);
  if (gate === 'hidden') return 'hidden';
  return gate === 'not-owed' ? 'reissue' : 'queue';
}

/** Body for `POST /batches/reprint-cart/entries`, straight off the generated contract. */
export type ReprintQueueAddBody = components['schemas']['ReprintQueueAddBody'];

/**
 * Build the queue-add body for the scanned garment.
 *
 * `blankRuined` is the ONE question the press answers (plan § 4.1), and its direction is
 * money-critical in one direction only. Answering "the blank survived" sends `transferOnly: true`,
 * which the storage layer keeps with `if_not_exists` (a one-way downgrade — only a later report
 * saying otherwise can undo it), the flush ANDs to true for the line, and mark-printed then filters
 * that item out of the blank consumables: zero blanks drawn, nothing on the pull list, and a presser
 * holding a fresh transfer with no shirt. The opposite mistake costs one spare blank. So the caller
 * must make the operator answer rather than defaulting, and this function never guesses.
 *
 * Returns `null` if the scan lacks identity or a source batch (defensive — callers gate on
 * `reprintDestination`).
 */
export function queueAddBodyFor(
  result: Pick<ScanResolveResult, 'storeId' | 'orderId' | 'lineId' | 'batchId' | 'owedRank'>,
  opts: { blankRuined: boolean },
): ReprintQueueAddBody | null {
  if (!result.storeId || !result.orderId || !result.lineId || !result.batchId) return null;
  if (result.owedRank == null) return null;
  return {
    storeId: result.storeId,
    orderId: result.orderId,
    lineId: result.lineId,
    // The queue's identity is the physical garment, and rank is what distinguishes copy 2 from copy
    // 5 of a qty-7 line. `owedRank` is already 1-based here (the resolver derives copyIndex from it).
    owedRank: result.owedRank,
    sourceBatchId: result.batchId,
    reason: 'PRESS_DEFECT',
    station: 'PRESS',
    // Omitted when the blank is ruined — the safe direction, and the same shape `reissueBodyFor`
    // uses. Sent as `true` ONLY on an explicit "the blank survived".
    ...(opts.blankRuined ? {} : { transferOnly: true }),
    // ⚠️ `ruinedLocations` IS DELIBERATELY NEVER SENT FROM HERE, and the omission is load-bearing:
    // absent means "unknown" means every placement reprints, which is the correct answer at the press.
    // A scan identifies a GARMENT, not a film — the kiosk has no placement to name — and the usual
    // press defect is a scorch, which ruins the shirt and takes both prints down with it.
    //
    // The cutting bench is the surface that narrows, because there the shirt is not in play at all.
    // Adding a film picker here is a separate change and needs its own review: it would put a new
    // choice in front of someone holding a hot press, and getting it wrong under-prints.
  };
}

/**
 * Build the reissue body for the scanned garment: the whole garment, one copy. `transferOnly` is left
 * off (default = draw a fresh replacement blank) — a defect caught at the press ruins the blank, not
 * just the film. Returns `null` if the scan lacks identity (defensive — callers gate on `canReprint`).
 */
export function reissueBodyFor(
  result: Pick<ScanResolveResult, 'storeId' | 'orderId' | 'lineId' | 'owedRank' | 'copyOf'>,
  opts: { clientToken?: string; override?: ReissueNotOwedReason } = {},
): ReissueBody | null {
  if (!result.storeId || !result.orderId || !result.lineId) return null;
  return {
    garments: [
      {
        storeId: result.storeId,
        orderId: result.orderId,
        lineId: result.lineId,
        copies: 1,
        // Name WHICH garment. The server's gate is rank-level because a bare count can't distinguish
        // "reprint 1 copy" from "reprint the 3rd copy of a line refunded to 1" — the latter is owed by
        // nobody. Without this the server has to fail closed on any partially-refunded line.
        ...(result.owedRank != null ? { owedRanks: [result.owedRank] } : {}),
        // `acknowledgedOwed` must equal the server's live owed count; `copyOf` IS that count as of this
        // scan, so no extra round trip is needed. A mismatch (a refund landed since) is a 400 telling
        // the operator to rescan — deliberately, so an override can never be stale.
        ...(opts.override && result.copyOf != null
          ? { notOwedOverride: { acknowledgedOwed: result.copyOf, reason: opts.override } }
          : {}),
      },
    ],
    ...(opts.clientToken ? { clientToken: opts.clientToken } : {}),
  };
}
